/**
 * Family Service
 * Handles family management operations
 * Based on Tim-Combo patterns
 */

import { apiClient, API_ENDPOINTS, isSuccessResponse } from './apiClient';

/**
 * Family Service API methods
 */
export const familyService = {
  /**
   * Get family details
   */
  async getFamily(familyId) {
    const response = await apiClient.get(API_ENDPOINTS.FAMILY.GET(familyId));
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to fetch family details');
  },

  /**
   * Create a new family
   */
  async createFamily(familyData) {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.CREATE, {
      name: familyData.name,
      description: familyData.description,
      settings: familyData.settings || {}
    });
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to create family');
  },

  /**
   * Update family details
   */
  async updateFamily(familyId, updates) {
    const response = await apiClient.put(
      API_ENDPOINTS.FAMILY.UPDATE(familyId),
      updates
    );
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update family');
  },

  /**
   * Generate a new family code
   */
  async generateFamilyCode() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No O/0, I/1
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  },

  /**
   * Create family with code
   */
  async createFamilyWithCode(familyData) {
    const familyCode = await this.generateFamilyCode();
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.CREATE, {
      name: familyData.name,
      description: familyData.description,
      family_code: familyCode,
      settings: familyData.settings || {}
    });

    if (isSuccessResponse(response)) {
      return {
        ...response.data?.Records?.[0] || response.data,
        family_code: familyCode
      };
    }
    throw new Error(response.message || 'Failed to create family');
  },

  /**
   * Join family by passcode
   */
  async joinFamilyByCode(familyCode) {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.JOIN, {
      family_code: familyCode.toUpperCase()
    });

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to join family. Invalid code or family not found.');
  },

  /**
   * Get family by code
   */
  async getFamilyByCode(familyCode) {
    const response = await apiClient.get(`${API_ENDPOINTS.FAMILY.CREATE}/by-code/${familyCode.toUpperCase()}`);

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Family not found with this code');
  },

  /**
   * Invite member to family (email-based)
   */
  async inviteMember(inviteData) {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.INVITE, {
      family_id: inviteData.familyId,
      email: inviteData.email,
      role: inviteData.role || 'member',
      user_type: inviteData.userType || 'parent',
      message: inviteData.message
    });

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to send invitation');
  },

  /**
   * Send email invite with family code (simple invitation)
   */
  async sendEmailInvite(email, familyCode, familyName) {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.INVITE, {
      email: email,
      family_code: familyCode,
      family_name: familyName,
      invite_type: 'email'
    });

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to send email invitation');
  },

  /**
   * Send email invite using Cognito AdminCreateUser
   * Creates a Cognito user account and sends invitation email
   */
  async sendCognitoUserInvite(email, familyId, role = 'parent', invitedBy = 'current_user') {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.USER_INVITE, {
      email: email,
      family_id: familyId,
      role: role,
      invited_by: invitedBy
    });

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to send Cognito user invitation');
  },

  /**
   * Accept family invitation
   */
  async acceptInvitation(inviteCode) {
    const response = await apiClient.post(API_ENDPOINTS.FAMILY.ACCEPT_INVITE, {
      invite_code: inviteCode
    });

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to accept invitation');
  },

  /**
   * Get family members
   */
  async getMembers(familyId) {
    const response = await apiClient.get(API_ENDPOINTS.FAMILY.MEMBERS(familyId));

    if (isSuccessResponse(response)) {
      // Validate response format - NO FALLBACKS that hide errors
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records; // Can be empty array if no results
    }
    throw new Error(response.message || 'Failed to fetch family members');
  },

  /**
   * Remove member from family
   */
  async removeMember(familyId, memberId) {
    const response = await apiClient.delete(
      API_ENDPOINTS.FAMILY.REMOVE_MEMBER(familyId, memberId)
    );
    
    if (isSuccessResponse(response)) {
      return true;
    }
    throw new Error(response.message || 'Failed to remove member');
  },

  /**
   * Update member role
   */
  async updateMemberRole(familyId, memberId, newRole) {
    const response = await apiClient.put(
      API_ENDPOINTS.FAMILY.UPDATE_MEMBER_ROLE(familyId, memberId),
      { role: newRole }
    );
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update member role');
  },

  /**
   * Get family statistics
   */
  async getFamilyStats(familyId) {
    const response = await apiClient.get(API_ENDPOINTS.FAMILY.STATS(familyId));
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to fetch family statistics');
  },

  /**
   * Get user's families (via entities endpoint)
   */
  async getUserFamilies() {
    const response = await apiClient.get(API_ENDPOINTS.ENTITIES.LIST, {
      params: { type: 'family' }
    });

    if (isSuccessResponse(response)) {
      // Validate response format - NO FALLBACKS that hide errors
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records; // Can be empty array if no results
    }
    throw new Error(response.message || 'Failed to fetch user families');
  },

  /**
   * Check if user is family admin
   */
  async isFamilyAdmin(familyId, userId) {
    try {
      const members = await this.getMembers(familyId);
      const member = members.find(m => m.user_id === userId);
      return member?.role === 'admin' || member?.role === 'owner';
    } catch (error) {
      console.error('Failed to check admin status:', error);
      return false;
    }
  },

  /**
   * Get family activity feed
   */
  async getActivityFeed(familyId, options = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.FAMILY.GET(familyId)}/activity`,
      { params: options }
    );

    if (isSuccessResponse(response)) {
      // Validate response format - NO FALLBACKS that hide errors
      if (!response.data || !Array.isArray(response.data.Records)) {
        throw new Error('API returned invalid response format - expected data.Records array');
      }
      return response.data.Records; // Can be empty array if no results
    }
    throw new Error(response.message || 'Failed to fetch activity feed');
  },

  /**
   * Update family settings
   */
  async updateSettings(familyId, settings) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.FAMILY.GET(familyId)}/settings`,
      settings
    );
    
    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update family settings');
  }
};

export default familyService;